import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Menu, X, PanelLeftClose, PanelLeft } from 'lucide-react';

// ─── Public types ─────────────────────────────────────────────────────────────

export type SidebarMode = 'compact' | 'expanded' | 'floating';

export interface SidebarItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

export interface SidebarProps {
  items?: SidebarItem[];
  /**
   * Initial mode when used uncontrolled. Defaults to 'expanded'.
   */
  defaultMode?: SidebarMode;
  /**
   * Controlled mode. When provided, the component becomes fully controlled;
   * pair with `onModeChange` to update the value.
   */
  mode?: SidebarMode;
  onModeChange?: (mode: SidebarMode) => void;
  className?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EXPANDED_WIDTH = 240; // px — design-token reference: sidebar-width-expanded
const COMPACT_WIDTH = 56;   // px — design-token reference: sidebar-width-compact
const FLOAT_BTN_SIZE = 48;  // px

// ─── Internal helpers ─────────────────────────────────────────────────────────

interface CompactTooltipProps {
  label: string;
  children: React.ReactNode;
}

function CompactTooltip({ label, children }: CompactTooltipProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: 8,
            zIndex: 9999,
            whiteSpace: 'nowrap',
            background: '#111827',
            color: '#fff',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '1.4',
            borderRadius: 6,
            padding: '4px 10px',
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

// Single sidebar item button — manages its own hover state to avoid verbose
// onMouseEnter/Leave with direct style mutations.
interface ItemButtonProps {
  item: SidebarItem;
  compact: boolean;
}

function ItemButton({ item, compact }: ItemButtonProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={item.onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: compact ? 0 : 10,
        justifyContent: compact ? 'center' : 'flex-start',
        padding: '8px 8px',
        borderRadius: 8,
        border: 'none',
        background: item.active ? '#eff6ff' : hovered ? '#f3f4f6' : 'transparent',
        color: item.active ? '#1d4ed8' : '#374151',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background 0.15s, color 0.15s',
        outline: 'none',
        textAlign: 'left',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item.icon}
      </span>
      {!compact && (
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.label}
        </span>
      )}
    </button>
  );
}

// The floating toggle button (top-right, offset outside sidebar box).
interface ToggleFloatBtnProps {
  pinned: boolean; // true = show "unpin" icon (go back from floating overlay)
  onClick: () => void;
}

function ToggleFloatBtn({ pinned, onClick }: ToggleFloatBtnProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      title={pinned ? 'Pin sidebar' : 'Collapse to floating button'}
      aria-label={pinned ? 'Pin sidebar' : 'Collapse to floating button'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'absolute',
        right: -12,
        top: 10,
        zIndex: 10,
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: '1px solid #e5e7eb',
        background: hovered ? '#f9fafb' : '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        color: hovered ? '#111827' : '#6b7280',
        transition: 'color 0.15s, background 0.15s',
      }}
    >
      {pinned ? (
        <PanelLeft size={12} strokeWidth={2} />
      ) : (
        <PanelLeftClose size={12} strokeWidth={2} />
      )}
    </button>
  );
}

// ─── Sidebar panel ────────────────────────────────────────────────────────────

interface SidebarPanelProps {
  items: SidebarItem[];
  panelMode: 'compact' | 'expanded';
  isOverlay: boolean;
  onToggleFloat: () => void;
}

function SidebarPanel({ items, panelMode, isOverlay, onToggleFloat }: SidebarPanelProps) {
  const compact = panelMode === 'compact';

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: compact ? COMPACT_WIDTH : EXPANDED_WIDTH,
        background: '#fff',
        borderRight: isOverlay ? 'none' : '1px solid #e5e7eb',
        boxShadow: isOverlay ? '4px 0 24px rgba(0,0,0,0.12)' : 'none',
        transition: 'width 0.2s ease',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Toggle-to-floating button — slightly offset outside the right edge */}
      <ToggleFloatBtn pinned={isOverlay} onClick={onToggleFloat} />

      <nav
        aria-label="Sidebar navigation"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {items.map((item) =>
          compact ? (
            <CompactTooltip key={item.id} label={item.label}>
              <ItemButton item={item} compact />
            </CompactTooltip>
          ) : (
            <ItemButton key={item.id} item={item} compact={false} />
          ),
        )}
      </nav>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function Sidebar({
  items = [],
  defaultMode = 'expanded',
  mode: controlledMode,
  onModeChange,
  className = '',
}: SidebarProps) {
  const isControlled = controlledMode !== undefined;

  const [internalMode, setInternalMode] = useState<SidebarMode>(defaultMode);
  const mode: SidebarMode = isControlled ? controlledMode! : internalMode;

  // Remember the last compact/expanded mode for restoring from floating.
  const prevPanelMode = useRef<'compact' | 'expanded'>(
    mode === 'floating' ? 'expanded' : (mode as 'compact' | 'expanded'),
  );

  // Whether the floating overlay panel is open.
  const [floatingOpen, setFloatingOpen] = useState(false);

  // Position of the floating button (top-left of viewport).
  const [floatPos, setFloatPos] = useState({ x: 16, y: 80 });

  const dragRef = useRef<{
    startClientX: number;
    startClientY: number;
    startPosX: number;
    startPosY: number;
    moved: boolean;
  } | null>(null);

  // Keep prevPanelMode in sync when mode changes externally.
  useEffect(() => {
    if (mode !== 'floating') {
      prevPanelMode.current = mode as 'compact' | 'expanded';
    }
  }, [mode]);

  const setMode = useCallback(
    (next: SidebarMode) => {
      if (!isControlled) setInternalMode(next);
      onModeChange?.(next);
    },
    [isControlled, onModeChange],
  );

  const goToFloating = useCallback(() => {
    prevPanelMode.current = mode as 'compact' | 'expanded';
    setFloatingOpen(false);
    setMode('floating');
  }, [mode, setMode]);

  const pinFromFloating = useCallback(() => {
    setFloatingOpen(false);
    setMode(prevPanelMode.current);
  }, [setMode]);

  // ── Floating button drag (pointer events) ──────────────────────────────────

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPosX: floatPos.x,
        startPosY: floatPos.y,
        moved: false,
      };
    },
    [floatPos],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startClientX;
    const dy = e.clientY - dragRef.current.startClientY;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      dragRef.current.moved = true;
    }
    const maxX = window.innerWidth - FLOAT_BTN_SIZE;
    const maxY = window.innerHeight - FLOAT_BTN_SIZE;
    setFloatPos({
      x: Math.max(0, Math.min(maxX, dragRef.current.startPosX + dx)),
      y: Math.max(0, Math.min(maxY, dragRef.current.startPosY + dy)),
    });
  }, []);

  const onPointerUp = useCallback(() => {
    if (dragRef.current && !dragRef.current.moved) {
      setFloatingOpen((v) => !v);
    }
    dragRef.current = null;
  }, []);

  // ── Render the visual display mode of the panel ────────────────────────────

  const panelMode: 'compact' | 'expanded' =
    mode === 'floating' ? prevPanelMode.current : (mode as 'compact' | 'expanded');

  // ── Floating mode ──────────────────────────────────────────────────────────

  if (mode === 'floating') {
    return (
      <>
        {/* Draggable floating button */}
        <button
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          title="Open sidebar"
          aria-label="Open sidebar"
          style={{
            position: 'fixed',
            left: floatPos.x,
            top: floatPos.y,
            zIndex: 1000,
            width: FLOAT_BTN_SIZE,
            height: FLOAT_BTN_SIZE,
            borderRadius: '50%',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            cursor: dragRef.current?.moved ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(37,99,235,0.35)',
            touchAction: 'none',
            userSelect: 'none',
            transition: 'background 0.15s',
          }}
        >
          {floatingOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        {/* Overlay */}
        {floatingOpen && (
          <>
            {/* Backdrop */}
            <div
              aria-hidden="true"
              onClick={() => setFloatingOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999,
                background: 'rgba(0,0,0,0.18)',
              }}
            />
            {/* Panel */}
            <div
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                bottom: 0,
                zIndex: 1000,
                display: 'flex',
              }}
            >
              <SidebarPanel
                items={items}
                panelMode={panelMode}
                isOverlay
                onToggleFloat={pinFromFloating}
              />
            </div>
          </>
        )}
      </>
    );
  }

  // ── Compact / Expanded mode ────────────────────────────────────────────────

  return (
    <div style={{ position: 'relative', height: '100%' }} className={className}>
      <SidebarPanel
        items={items}
        panelMode={panelMode}
        isOverlay={false}
        onToggleFloat={goToFloating}
      />
    </div>
  );
}
